import { PAGINATION } from '@/constants/pagination.constant';
import { MessageType } from '@/types/common';
import { createAppSlice } from '../createAppSlice';
import { transformerArray } from '../transformer';
import { Message } from '@/enums/message.enum';
import { MESSAGE } from '@/constants/message.constant';

type HealthStatusSliceState = {
  loading: boolean;
  listHealthStatus: HealthStatus[];
  listFoodRecommendBasedOnHealthStatus: FoodRecommendBasedOnHealthStatus[];
  message?: MessageType;
  healthStatusPagination: Pagination;
  foodRecommendBasedOnHealthStatusPagination: Pagination;
  importingError?: boolean;
};

const initialState: HealthStatusSliceState = {
  loading: false,
  listHealthStatus: [],
  listFoodRecommendBasedOnHealthStatus: [],
  healthStatusPagination: PAGINATION,
  foodRecommendBasedOnHealthStatusPagination: PAGINATION,
  message: undefined,
  importingError: undefined
};

export const healthStatusSlice = createAppSlice({
  name: 'healthStatus',
  initialState,
  reducers: (create) => ({
    getListHealthStatus: create.asyncThunk(
      async (body?: Record<string, any>) => {
        const response = await apiFetcher.get(
          'health-management/health-status/list',
          {
            params: body
          }
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listHealthStatus = transformerArray(action.payload.results);
          state.healthStatusPagination = action.payload.paging;
        },
        rejected: (state) => {
          state.loading = false;
          state.listHealthStatus = [];
          state.healthStatusPagination = PAGINATION;
        }
      }
    ),
    importHealthStatus: create.asyncThunk(
      async (file: File) => {
        try {
          const formData = new FormData();
          formData.append('file', file);

          const response = await apiFetcher.post(
            'health-management/health-status/import',
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            }
          );
          return response.data;
        } catch (error: any) {
          throw new Error(error?.response?.data?.message || error?.data?.message);
        }
      },
      {
        pending: (state) => {
          state.loading = true;
          state.importingError = false;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.importingError = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.CREATE_SUCCESS
          };
        },
        rejected: (state, action) => {
          state.loading = false;
          state.importingError = true;
          state.message = {
            type: Message.ERROR,
            message: action.error?.message || MESSAGE.CREATE_FAIL
          };
        }
      }
    ),
    getFoodRecommendBasedOnHealthStatus: create.asyncThunk(
      async (body?: Record<string, any>) => {
        const response = await apiFetcher.get(
          'health-management/food-recommendation/list',
          {
            params: body
          }
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listFoodRecommendBasedOnHealthStatus = transformerArray(
            action.payload.results
          );
          state.foodRecommendBasedOnHealthStatusPagination =
            action.payload.paging;
        },
        rejected: (state) => {
          state.loading = false;
          state.listFoodRecommendBasedOnHealthStatus = [];
          state.foodRecommendBasedOnHealthStatusPagination = PAGINATION;
        }
      }
    ),
    deleteFoodRecommendation: create.asyncThunk(
      async (id: number) => {
        const response = await apiFetcher.post(
          `health-management/food-recommendation?ID=${id}`
        );
        return response;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listFoodRecommendBasedOnHealthStatus =
            state.listFoodRecommendBasedOnHealthStatus.filter(
              (item) => item.id !== action.payload.id
            );
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.DELETE_SUCCESS || 'Xoá thành công!'
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.DELETE_FAIL || 'Xoá thất bại!'
          };
        }
      }
    ),
    importFoodRecommendation: create.asyncThunk(
      async ({ file, healthStatusId }: { file: File; healthStatusId: string }) => {
        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('health_status', healthStatusId)

          const response = await apiFetcher.post(
            'health-food-recommendation/import',
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            }
          );
          return response.data;
        } catch (error: any) {
          throw new Error(error?.response?.data?.message || error?.data?.message);
        }
      },
      {
        pending: (state) => {
          state.loading = true;
          state.importingError = false;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.importingError = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.CREATE_SUCCESS
          };
        },
        rejected: (state, action) => {
          state.loading = false;
          state.importingError = true;
          state.message = {
            type: Message.ERROR,
            message: action.error?.message || MESSAGE.CREATE_FAIL
          };
        }
      }
    ),
    resetMessages: create.reducer((state) => {
      state.message = undefined;
    })
  }),
  selectors: {
    watchHealthStatusList: (state) => state.listHealthStatus,
    watchHealthStatusLoading: (state) => state.loading,
    watchHealthStatusMessage: (state) => state.message,
    watchHealthStatusPagination: (state) => state.healthStatusPagination,

    watchFoodRecommendBasedOnHealthStatusList: (state) =>
      state.listFoodRecommendBasedOnHealthStatus,
    watchFoodRecommendBasedOnHealthStatusLoading: (state) => state.loading,
    watchFoodRecommendBasedOnHealthStatusMessage: (state) => state.message,
    watchFoodRecommendBasedOnHealthStatusPagination: (state) =>
      state.foodRecommendBasedOnHealthStatusPagination,

    watchImportingError: (state) => state.importingError
  }
});

export const {
  watchHealthStatusList,
  watchHealthStatusLoading,
  watchHealthStatusMessage,
  watchHealthStatusPagination,

  watchFoodRecommendBasedOnHealthStatusList,
  watchFoodRecommendBasedOnHealthStatusLoading,
  watchFoodRecommendBasedOnHealthStatusMessage,
  watchFoodRecommendBasedOnHealthStatusPagination,

  watchImportingError
} = healthStatusSlice.selectors;

export const {
  getListHealthStatus,
  importHealthStatus,
  resetMessages,
  getFoodRecommendBasedOnHealthStatus,
  deleteFoodRecommendation,
  importFoodRecommendation
} = healthStatusSlice.actions;
